import { useState } from "react";
import {
  PATTERN_SLOTS,
  type Pattern,
  type PatternId,
  type PatternSlot,
  type TrackNumber,
  formatPatternId,
} from "@midi-fantom-utils/midi-util";
import type { SoundfontSource } from "@midi-fantom-utils/player-core";
import { PatternMiniPlayer } from "./PatternMiniPlayer.js";

export type PatternScreenProps = {
  patterns: ReadonlyMap<PatternId, Pattern>;
  source: SoundfontSource;
  onEdit: (patternId: PatternId) => void;
  onDelete: (patternId: PatternId) => void;
};

const TRACK_NUMBERS: TrackNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

export const PatternScreen = ({ patterns, source, onEdit, onDelete }: PatternScreenProps) => {
  const [page, setPage] = useState<0 | 1>(0);
  const startTrack = page === 0 ? 1 : 9;
  const endTrack = page === 0 ? 8 : 16;
  const visibleTracks = TRACK_NUMBERS.filter((t) => t >= startTrack && t <= endTrack);

  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <h2 className="card__title" style={{ margin: 0 }}>Слоты паттернов (Fantom)</h2>
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          <button
            type="button"
            className={`btn ${page === 0 ? "btn--primary" : ""}`}
            onClick={() => setPage(0)}
          >
            Tr 1–8
          </button>
          <button
            type="button"
            className={`btn ${page === 1 ? "btn--primary" : ""}`}
            onClick={() => setPage(1)}
          >
            Tr 9–16
          </button>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th style={cellStyle("head")}>Tr</th>
              {PATTERN_SLOTS.map((s) => (
                <th key={s} style={cellStyle("head")}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleTracks.map((t) => (
              <tr key={t}>
                <td style={cellStyle("rowHead")}>{t}</td>
                {PATTERN_SLOTS.map((s) => {
                  const id = formatPatternId(t, s);
                  const pat = patterns.get(id);
                  return (
                    <td key={s} style={cellStyle(pat ? "filled" : "empty")}>
                      {pat ? <PatternCell pat={pat} source={source} onEdit={onEdit} onDelete={onDelete} /> : <EmptyCell />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const cellStyle = (kind: "head" | "rowHead" | "empty" | "filled"): React.CSSProperties => {
  const base: React.CSSProperties = {
    border: "1px solid var(--border)",
    padding: 6,
    verticalAlign: "top",
  };
  if (kind === "head") return { ...base, color: "var(--text-muted)", fontWeight: 500 };
  if (kind === "rowHead") return { ...base, fontWeight: 600, color: "var(--text-muted)", width: 28, textAlign: "center" };
  if (kind === "empty") return { ...base, color: "var(--text-muted)", background: "var(--surface-2)", textAlign: "center" };
  return base;
};

const EmptyCell = () => <span style={{ color: "var(--text-muted)" }}>·</span>;

const PatternCell = ({
  pat,
  source,
  onEdit,
  onDelete,
}: {
  pat: Pattern;
  source: SoundfontSource;
  onEdit: (id: PatternId) => void;
  onDelete: (id: PatternId) => void;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <div style={{ fontWeight: 600 }}>{pat.name}</div>
    <div className="muted">
      {pat.lengthMeasures} тактов · {pat.noteCount} нот
    </div>
    <div style={{ display: "flex", gap: 4 }}>
      <PatternMiniPlayer pattern={pat} source={source} />
      <button
        type="button"
        className="btn"
        style={{ padding: "2px 6px", fontSize: 11 }}
        onClick={() => onEdit(pat.id)}
        aria-label="Редактировать"
      >
        ✎
      </button>
      <button
        type="button"
        className="btn btn--ghost"
        style={{ padding: "2px 6px", fontSize: 11 }}
        onClick={() => onDelete(pat.id)}
        aria-label="Удалить"
      >
        🗑
      </button>
    </div>
  </div>
);
